import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {isPostTranslationPreviewEnabled} from 'config';
import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import {isAdmin} from 'utils/role';
import request from 'utils/request';
import './style.scss';

const ENDPOINT = 'translations/admin/settings';
const basicUserSelector = makeSelectBasicUser();
const PROVIDER_LABELS = {
  gemini: 'Gemini', deepseek: 'DeepSeek', openai_luna: 'OpenAI Luna', google_nmt: 'Google NMT',
};
const sourceLabel = (credential) => {
  if (!credential.configured) return 'Not configured';
  return credential.source === 'settings' ? 'Saved in settings' : 'Configured on server';
};
const readSettings = (response) => {
  const data = response?.data || response;
  if (!Number.isInteger(data?.revision) || data.revision < 0 ||
      !Array.isArray(data.models) || !Array.isArray(data.credentials) ||
      typeof data.selectedModelSlot !== 'string' ||
      !(data.previewEnabled === null || typeof data.previewEnabled === 'boolean')) {
    throw new Error('Invalid settings response.');
  }
  return {
    revision: data.revision, previewEnabled: data.previewEnabled,
    selectedModelSlot: data.selectedModelSlot, models: data.models, credentials: data.credentials,
    tokenStorageAvailable: data.tokenStorageAvailable === true, updatedAt: data.updatedAt,
  };
};

const TranslationSettings = () => {
  const user = useSelector(basicUserSelector);
  const allowed = isAdmin(user?.roles);
  const authToken = user?.authToken;
  const [settings, setSettings] = useState(null);
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [selectedModelSlot, setSelectedModelSlot] = useState('');
  const [tokens, setTokens] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [conflict, setConflict] = useState(false);
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const pendingSave = useRef(false);

  const applySettings = useCallback((next) => {
    setSettings(next);
    setPreviewEnabled(next.previewEnabled ?? isPostTranslationPreviewEnabled());
    setSelectedModelSlot(next.selectedModelSlot);
    setTokens({});
    setConflict(false);
  }, []);
  const load = useCallback(async () => {
    if (!allowed || !authToken || pendingSave.current) return;
    requestVersion.current += 1;
    const {current} = requestVersion;
    setLoading(true);
    setError('');
    setSaved(false);
    try {
      const next = readSettings(await request(ENDPOINT, authToken));
      if (mounted.current && current === requestVersion.current) applySettings(next);
    } catch {
      if (mounted.current && current === requestVersion.current) {
        setError('Translation settings could not be loaded. Please try again.');
      }
    } finally {
      if (mounted.current && current === requestVersion.current) setLoading(false);
    }
  }, [allowed, authToken, applySettings]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestVersion.current += 1; };
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = Boolean(settings && (
    previewEnabled !== (settings.previewEnabled ?? isPostTranslationPreviewEnabled()) ||
    selectedModelSlot !== settings.selectedModelSlot || Object.values(tokens).some((token) => token.length > 0)
  ));
  const validationError = Object.values(tokens).some((token) => token && (!token.trim() || /[\r\n]/.test(token)))
    ? 'Enter a valid API token without line breaks, or leave the field empty to keep the current token.' : '';
  const canSave = allowed && authToken && dirty && !loading && !saving && !conflict && !validationError;
  const selectedModel = settings?.models.find((model) => model.id === selectedModelSlot);

  const changePreview = useCallback((event) => {
    setPreviewEnabled(event.target.checked);
    setSaved(false);
  }, []);
  const changeModel = useCallback((event) => {
    setSelectedModelSlot(event.target.value);
    setSaved(false);
  }, []);
  const changeToken = useCallback((event) => {
    const {name, value} = event.target;
    setTokens((previous) => ({...previous, [name]: value}));
    setSaved(false);
  }, []);
  const discard = useCallback(() => {
    if (settings) applySettings(settings);
    setError('');
    setSaved(false);
  }, [settings, applySettings]);
  const save = useCallback(async (event) => {
    event.preventDefault();
    if (!canSave || pendingSave.current) return;
    pendingSave.current = true;
    setSaving(true);
    setError('');
    setSaved(false);
    const replacements = Object.fromEntries(Object.entries(tokens).filter(([, value]) => value.length > 0));
    try {
      const body = {expectedRevision: settings.revision, previewEnabled, selectedModelSlot};
      if (Object.keys(replacements).length) body.tokens = replacements;
      const next = readSettings(await request(ENDPOINT, authToken, {method: 'PUT', body: JSON.stringify(body)}));
      if (mounted.current) {
        applySettings(next);
        setSaved(true);
      }
    } catch (saveError) {
      if (mounted.current) {
        const code = saveError.code || saveError.payload?.error;
        if (code === 'TRANSLATION_REGISTRY_NOT_READY') {
          setError('This model needs server setup before it can be used. Existing settings have not changed.');
        } else if (code === 'TOKEN_STORAGE_UNAVAILABLE') {
          setError('Secure token storage is unavailable. Existing settings have not changed.');
        } else if (saveError.status === 409) {
          setConflict(true);
          setError('Another administrator changed these settings. Reload the latest settings before saving again.');
        } else if (saveError.status === 400) {
          setError('Settings were not saved. Check the selected model and its credentials, then try again.');
        } else if (saveError.status === 403) {
          setError('Only administrators can change translation settings.');
        } else {
          setError('Settings could not be saved. Your changes are still here; please try again.');
        }
      }
    } finally {
      pendingSave.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [canSave, tokens, settings, previewEnabled, selectedModelSlot, authToken, applySettings]);

  let statusLabel = 'Administrator access only';
  if (loading) statusLabel = 'Loading settings…';
  else if (saved) statusLabel = 'Settings saved.';
  else if (dirty) statusLabel = 'You have unsaved changes.';

  if (!allowed) return <p role="alert">Only administrators can access translation settings.</p>;

  return (
    <section className="translation-settings" aria-labelledby="translation-settings-title" aria-busy={loading || saving}>
      <form onSubmit={save}>
        <div className="translation-settings__heading">
          <div><h1 id="translation-settings-title">Translation Settings</h1>
            <p>Manage the translation preview, AI model and API tokens.</p></div>
          <div className="translation-settings__actions">
            <button type="button" onClick={load} disabled={loading || saving || (dirty && !conflict)}>Reload</button>
            {dirty && !conflict && <button type="button" onClick={discard} disabled={loading || saving}>Discard changes</button>}
            <button type="submit" className="translation-settings__save" disabled={!canSave}>{saving ? 'Saving…' : 'Save settings'}</button>
          </div>
        </div>
        {error && <p className="translation-settings__message translation-settings__message--error" role="alert">{error}</p>}
        {validationError && <p className="translation-settings__message translation-settings__message--error" role="alert">{validationError}</p>}
        <div className="translation-settings__status" role="status">
          {statusLabel}
        </div>
        {settings && <fieldset disabled={loading || saving || conflict}>
          <legend className="sr-only">Translation configuration</legend>
          <section className="translation-settings__panel" aria-labelledby="translation-settings-preview">
            <div className="translation-settings__section-heading"><h2 id="translation-settings-preview">Translation preview</h2>
              <span className="translation-settings__badge">{previewEnabled ? 'Enabled' : 'Background'}</span></div>
            <label className="translation-settings__toggle">
              <input type="checkbox" checked={previewEnabled} onChange={changePreview} />
              <span>Open Translation Preview in a new window after Lock</span>
            </label>
            <p>The article list stays open. When enabled, locking opens the translation preview in a new window; when disabled, translation runs in the background.</p>
            {settings.previewEnabled === null && <small>The current deployment default applies until this setting is saved.</small>}
          </section>
          <section className="translation-settings__panel" aria-labelledby="translation-settings-model">
            <h2 id="translation-settings-model">AI model</h2>
            <p>Choose from the existing translation models. New jobs use this selection; existing jobs keep their original model.</p>
            <label className="translation-settings__field" htmlFor="translation-model-select">
              <span>Translation model</span>
              <select id="translation-model-select" value={selectedModelSlot} onChange={changeModel}>
                {settings.models.map((model) => <option key={model.id} value={model.id}>{PROVIDER_LABELS[model.provider] || model.provider} · {model.model}</option>)}
              </select>
            </label>
            {selectedModel && <small>{selectedModel.configured ? 'This model is configured.' : 'This model is not ready. Check its credentials and server configuration before saving.'}</small>}
          </section>
          <section className="translation-settings__panel" aria-labelledby="translation-settings-tokens">
            <h2 id="translation-settings-tokens">API tokens</h2>
            <p>Saved tokens are encrypted and are never displayed. Leave a field empty to keep its current token.</p>
            {!settings.tokenStorageAvailable && <p className="translation-settings__message">Token editing is unavailable until secure storage is configured on the server. Preview and model settings can still be saved.</p>}
            <div className="translation-settings__credentials">
              {settings.credentials.map((credential) => (
                <div key={credential.provider} className="translation-settings__credential">
                  <div className="translation-settings__section-heading">
                    <h3>{PROVIDER_LABELS[credential.provider] || credential.provider}</h3>
                    <span className={`translation-settings__badge ${credential.configured ? 'translation-settings__badge--configured' : ''}`}>{sourceLabel(credential)}</span>
                  </div>
                  {credential.editable ? <label className="translation-settings__field" htmlFor={`translation-token-${credential.provider}`}>
                    <span>API token</span>
                    <input
                      id={`translation-token-${credential.provider}`}
                      aria-label={`${PROVIDER_LABELS[credential.provider] || credential.provider} API token`}
                      type="password"
                      name={credential.provider}
                      value={tokens[credential.provider] || ''}
                      onChange={changeToken}
                      autoComplete="new-password"
                      spellCheck={false}
                      maxLength={4096}
                      disabled={!settings.tokenStorageAvailable}
                      placeholder={credential.configured ? 'Enter a replacement token' : 'Enter an API token'}
                    />
                  </label> : <small>Credentials are managed on the server.</small>}
                </div>
              ))}
            </div>
          </section>
        </fieldset>}
      </form>
    </section>
  );
};

export default TranslationSettings;
